import { LitElement, css, html, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { shared } from '../styles/shared.js';

export type DigitTileVariant = 'digit' | 'icon' | 'mode';

/**
 * Foundational reusable tile. Used by the parent gate, recognition, counting,
 * addition, and subtraction modes — any time we need a large tappable choice.
 *
 * Layout/visual classes (`.tile`, `.tile--digit`, `.tile--icon`, `.tile--mode`,
 * `.tile--flash`) come from `src/styles/components.css` via the shared
 * stylesheet. Component-specific styles below stay minimal.
 */
@customElement('digit-tile')
export class DigitTile extends LitElement {
  static override styles = [
    shared,
    css`
      :host {
        display: inline-block;
      }

      :host([disabled]) button {
        opacity: 0.4;
        pointer-events: none;
      }

      button {
        all: unset;
      }
    `,
  ];

  /** The variant determines which `.tile--*` class is applied. */
  @property({ reflect: true }) variant: DigitTileVariant = 'digit';

  /** Numeric value to display (used by `variant: 'digit'`). */
  @property({ type: Number }) value: number | null = null;

  /** Accessible label override; falls back to `String(value)` for digit. */
  @property() label?: string;

  /** Disable interaction. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Flash animation toggle (auto-resets after the animation completes). */
  @property({ type: Boolean, reflect: true }) flash = false;

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('flash') && this.flash) {
      window.setTimeout(() => {
        this.flash = false;
      }, 250);
    }
  }

  private onClick(event: Event): void {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.dispatchEvent(
      new CustomEvent('tile-tap', {
        bubbles: true,
        composed: true,
        detail: { value: this.value },
      }),
    );
  }

  override render() {
    const classes = {
      tile: true,
      'tile--digit': this.variant === 'digit',
      'tile--icon': this.variant === 'icon',
      'tile--mode': this.variant === 'mode',
      'tile--flash': this.flash,
    };
    const ariaLabel = this.label ?? (this.value !== null ? String(this.value) : '');
    return html`
      <button
        type="button"
        class=${classMap(classes)}
        aria-label=${ariaLabel}
        ?disabled=${this.disabled}
        @click=${this.onClick}
      >
        ${this.variant === 'digit' && this.value !== null ? this.value : html`<slot></slot>`}
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'digit-tile': DigitTile;
  }
}
